import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Paper,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  PersonAdd,
  Save,
  ArrowBack,
  NavigateNext,
  CheckCircle,
  Search,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { PatternFormat } from "react-number-format";
import { useFormik } from "formik";
import dayjs from "dayjs";
import "dayjs/locale/es";

import {
  postDirectStudentRegistration,
  getSearchClientsByDocument
} from "../../../services/apiStudents";
import {
  fetchTypeTable,
  selectMaintenances,
} from "../../../store/slices/maintenances";
import ModalMessages from "../../layout/modal-messages";

// Tipos de estudiante válidos para alta directa
const STUDENT_TYPES = [
  { id: 2, description: "EMPLEADO" },
  { id: 4, description: "FAMILIAR EMPLEADO" },
  { id: 5, description: "FAMILIAR AFILIADO" },
];

// Situaciones válidas ante UTEDYC para alta directa.
// El backend rechaza con INVALID_SITUATION si se envía situationId 2 (Aportante) o 3 (Externo).
const VALID_SITUATION_IDS = [1, 4, 5, 6];

// Tipos de relación familiar
const RELATIONSHIP_TYPES = [
  { id: 2, description: "HIJO" },
  { id: 3, description: "CÓNYUGE" }
];

// Validación de dígito verificador CUIL/CUIT con algoritmo módulo 11
const isValidCuil = (cuil) => {
  const clean = cuil.replace(/-/g, "");
  if (clean.length !== 11 || !/^\d{11}$/.test(clean)) return false;

  const multipliers = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(clean[i]) * multipliers[i];
  }
  const remainder = sum % 11;
  const expectedDigit = remainder === 0 ? 0 : remainder === 1 ? 9 : 11 - remainder;
  return parseInt(clean[10]) === expectedDigit;
};

const validate = (values) => {
  const errors = {};

  if (!values.cuil || values.cuil.trim() === "") {
    errors.cuil = "El CUIL es obligatorio";
  } else if (values.cuil.replace(/-/g, "").length !== 11) {
    errors.cuil = "El CUIL debe tener 11 dígitos";
  } else if (!isValidCuil(values.cuil)) {
    errors.cuil = "El CUIL ingresado no es válido (dígito verificador incorrecto)";
  }

  if (!values.firstName || values.firstName.trim() === "") {
    errors.firstName = "Requerido";
  }

  if (!values.lastName || values.lastName.trim() === "") {
    errors.lastName = "Requerido";
  }

  if (!values.email || values.email.trim() === "") {
    errors.email = "Requerido";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = "Formato de email inválido";
  }

  if (!values.birthDate) {
    errors.birthDate = "Requerido";
  }

  if (!values.genderId || values.genderId === 0) {
    errors.genderId = "Requerido";
  }

  if (!values.nationality || values.nationality === 0) {
    errors.nationality = "Requerido";
  }

  if (!values.academicId || values.academicId === 0) {
    errors.academicId = "Requerido";
  }

  if (!values.labouralSituationId || values.labouralSituationId === 0) {
    errors.labouralSituationId = "Requerido";
  }

  if (!values.situationId || values.situationId === 0) {
    errors.situationId = "Requerido";
  }

  if (!values.typeStudentId || values.typeStudentId === 0) {
    errors.typeStudentId = "Debe seleccionar un tipo de alumno válido";
  }

  // username: el backend lo auto-genera desde el email si no se envía;
  // solo validamos si el operador lo completó (para evitar enviar un valor inválido)
  if (values.username && values.username.trim() !== "") {
    if (values.username.length < 3 || values.username.length > 50) {
      errors.username = "Debe tener entre 3 y 50 caracteres";
    }
  }

  if (!values.fullAddress || values.fullAddress.trim() === "") {
    errors.fullAddress = "Requerido";
  } else if (values.fullAddress.length < 5) {
    errors.fullAddress = "Debe tener al menos 5 caracteres";
  }

  if (!values.provinceId || values.provinceId === 0) {
    errors.provinceId = "Requerido";
  }

  if (!values.areaCode || values.areaCode.trim() === "") {
    errors.areaCode = "Requerido";
  } else if (!/^[0-9]+$/.test(values.areaCode)) {
    errors.areaCode = "Solo números";
  }

  if (!values.phone || values.phone.trim() === "") {
    errors.phone = "Requerido";
  } else if (!/^[0-9]+$/.test(values.phone)) {
    errors.phone = "Solo números";
  } else if (values.phone.length < 6 || values.phone.length > 10) {
    errors.phone = "Debe tener entre 6 y 10 dígitos";
  }

  // Familiar = situationId 4 (Familiar Afiliado) o 5 (Familiar Empleado)
  const isFamiliar = values.situationId === 4 || values.situationId === 5;

  if (isFamiliar) {
    // Tanto relatedClientId como relatedClientName son opcionales.
    // El operador puede: (a) buscar y seleccionar un familiar, (b) ingresar nombre manual, o (c) no completar nada.
    // Solo validamos relationshipType si seleccionó un clientId del buscador.
    if (values.relatedClientId && (!values.relationshipType || values.relationshipType === 0)) {
      errors.relationshipType = "Debe especificar el tipo de relación familiar";
    }
  }

  return errors;
};

const DirectStudentRegistration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const typeTables = useSelector(selectMaintenances);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [createdStudent, setCreatedStudent] = useState(null);

  const [searchingRelated, setSearchingRelated] = useState(false);
  const [relatedClients, setRelatedClients] = useState([]);
  const [relatedSearchTerm, setRelatedSearchTerm] = useState("");
  const [hasSearchedRelated, setHasSearchedRelated] = useState(false);

  const formik = useFormik({
    initialValues: {
      cuil: "",
      firstName: "",
      lastName: "",
      birthDate: null,
      genderId: 0,
      email: "",
      typeStudentId: 0,
      username: "",
      nationality: 0,
      academicId: 0,
      labouralSituationId: 0,
      situationId: 0,
      fullAddress: "",
      provinceId: 0,
      areaCode: "",
      phone: "",
      relatedClientId: null,
      relationshipType: 0,
      relatedClientName: "",
    },
    validate,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const cuilSinGuiones = values.cuil.replace(/-/g, "");
        const dniSolo = cuilSinGuiones.substring(2, cuilSinGuiones.length - 1);

        const requestData = {
          student: {
            Username: values.username.trim(),
            Email: values.email.trim(),
            Clients: {
              Email: values.email.trim(),
              Nationality: values.nationality,
              CliNaturals: {
                FirstName: values.firstName.trim(),
                LastName: values.lastName.trim(),
              },
              CliDocuments: [
                {
                  Document: cuilSinGuiones,
                  TypeDocumentId: 2,
                },
                {
                  Document: dniSolo,
                  TypeDocumentId: 1,
                },
              ],
              CliSocialEconomics: {
                BirthDate: dayjs(values.birthDate).format("YYYY-MM-DDTHH:mm:ss"),
                GenderId: values.genderId,
                AcademicId: values.academicId,
                LabouralSituationId: values.labouralSituationId,
                SituationId: values.situationId,
              },
              Students: {
                TypeStudentId: values.typeStudentId,
                NickName: values.firstName.trim(),
              },
              CliAddress: [
                {
                  FullAddress: values.fullAddress.trim(),
                  TypeAddress: 1,
                  Province: values.provinceId,
                  Country: 1,
                  AreaCode: values.areaCode.trim(),
                  Phone: values.phone.trim(),
                },
              ],
              CliContacts: [
                {
                  TypeContact: 3,
                  Contact: values.phone.trim(),
                  ContactName: values.firstName.trim() + " " + values.lastName.trim(),
                  Email: values.email.trim(),
                  Phone: values.phone.trim(),
                  AreaCode: values.areaCode.trim(),
                },
              ],
            },
          },
        };

        // Familiar = situationId 4 (Familiar Afiliado) o 5 (Familiar Empleado)
        const isFamiliar = values.situationId === 4 || values.situationId === 5;

        if (isFamiliar) {
          requestData.RelatedClientId = values.relatedClientId || null;
          requestData.RelationshipType = (values.relatedClientId && values.relationshipType !== 0)
            ? values.relationshipType
            : null;
          requestData.RelatedClientName = values.relatedClientName?.trim() || null;
        }

        console.log("Request enviado al backend:", JSON.stringify(requestData, null, 2));

        const response = await postDirectStudentRegistration(requestData);

        if (response.data && response.data.isValid) {
          console.log("Alumno creado - respuesta del backend:", response.data.data);
          setCreatedStudent(response.data.data);
          setSubmitSuccess(true);
          formik.resetForm();
        } else {
          console.log("Errores del backend:", response.data?.errors);
          setSubmitError(response.data?.errors || ["Error al registrar el alumno"]);
        }
      } catch (error) {
        console.error("Error registering student:", error);
        console.log("Error completo:", error.data);

        if (error.data && error.data.errors && Array.isArray(error.data.errors)) {
          console.log("Errores de validación del backend:", error.data.errors);
          setSubmitError(error.data.errors);
        } else if (error.data && error.data.errors) {
          const validationErrors = Object.entries(error.data.errors).map(
            ([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
          );
          setSubmitError(validationErrors);
        } else if (error.data && error.data.title) {
          setSubmitError([error.data.title]);
        } else if (error.status === 400) {
          setSubmitError(["Error de validación. Verifique los datos ingresados."]);
        } else if (error.status === 401) {
          setSubmitError(["No autorizado. Inicie sesión nuevamente."]);
        } else {
          setSubmitError(["Error de conexión. Intente nuevamente."]);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    dispatch(fetchTypeTable(49));
    dispatch(fetchTypeTable(16));
    dispatch(fetchTypeTable(45));
    dispatch(fetchTypeTable(14));
    dispatch(fetchTypeTable(20));
    dispatch(fetchTypeTable(17));
  }, [dispatch]);

  const handleGoBack = () => {
    navigate("/backoffice/students");
  };

  const handleCreateAnother = () => {
    setSubmitSuccess(false);
    setCreatedStudent(null);
    setRelatedClients([]);
    setRelatedSearchTerm("");
    setHasSearchedRelated(false);
    formik.resetForm();
    formik.setFieldValue("relatedClientName", "");
  };

  const handleSearchRelatedClient = async () => {
    if (!relatedSearchTerm || relatedSearchTerm.trim() === "") {
      return;
    }

    setSearchingRelated(true);

    try {
      const response = await getSearchClientsByDocument(relatedSearchTerm.replace(/-/g, ""));

      if (response.data && response.data.isValid && response.data.data) {
        setRelatedClients(Array.isArray(response.data.data) ? response.data.data : [response.data.data]);
      } else {
        setRelatedClients([]);
      }
    } catch (error) {
      console.error("Error searching related client:", error);
      setRelatedClients([]);
    } finally {
      setSearchingRelated(false);
      setHasSearchedRelated(true);
    }
  };

  const onlyLetters = (event) => {
    const onlyLettersPattern = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
    if (!onlyLettersPattern.test(event.key)) {
      event.preventDefault();
    }
  };

  if (submitSuccess) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box textAlign="center">
            <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h4" color="success.main" gutterBottom>
              ¡Alumno Registrado Exitosamente!
            </Typography>

            {createdStudent && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Alert severity="info" sx={{ textAlign: "left", mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Nombre:</strong> {createdStudent.clients?.cliNaturals?.firstName || ""} {createdStudent.clients?.cliNaturals?.lastName || ""}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {createdStudent.email || createdStudent.clients?.email || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tipo:</strong> {
                      createdStudent.clients?.students?.typeStudent?.description || (
                        createdStudent.clients?.students?.typeStudentId === 2 ? "EMPLEADO" :
                          createdStudent.clients?.students?.typeStudentId === 4 ? "FAMILIAR EMPLEADO" :
                            createdStudent.clients?.students?.typeStudentId === 5 ? "FAMILIAR AFILIADO" :
                              createdStudent.clients?.students?.typeStudentId === 1 ? "AFILIADO/EMPLEADO/FAMILIAR" :
                                "N/A"
                      )
                    }
                  </Typography>
                  {createdStudent.clientId && (
                    <Typography variant="body2">
                      <strong>ID Cliente:</strong> {createdStudent.clientId}
                    </Typography>
                  )}
                </Alert>

                <Alert severity="success">
                  Puede vincular este alumno a cursos virtuales desde su ficha
                </Alert>
              </Box>
            )}

            <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateAnother}
                startIcon={<PersonAdd />}
              >
                Registrar Otro Alumno
              </Button>
              <Button
                variant="outlined"
                onClick={handleGoBack}
                startIcon={<ArrowBack />}
              >
                Volver al Listado
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleGoBack}
          sx={{ cursor: "pointer" }}
        >
          Alumnos
        </Link>
        <Typography color="text.primary">Alta Directa</Typography>
      </Breadcrumbs>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box textAlign="center" sx={{ mb: 4 }}>
          <PersonAdd color="primary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" color="primary" gutterBottom>
            Alta Directa de Alumno
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Registra empleados y familiares directamente sin vincular a un curso
          </Typography>
        </Box>

        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {Array.isArray(submitError)
              ? submitError.map((err, idx) => {
                let errorText = '';
                if (typeof err === 'object' && err !== null) {
                  errorText = err.errorMessage || err.message || JSON.stringify(err);
                } else {
                  errorText = String(err);
                }
                return (
                  <Typography key={idx} variant="body2" component="div" sx={{ mb: 0.5 }}>
                    {errorText}
                  </Typography>
                );
              })
              : <Typography variant="body2">{String(submitError)}</Typography>}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Sección 1: Tipo de Alumno */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                1. Tipo de Alumno
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                size="small"
                error={formik.touched.typeStudentId && Boolean(formik.errors.typeStudentId)}
              >
                <InputLabel>Tipo de Alumno *</InputLabel>
                <Select
                  id="typeStudentId"
                  name="typeStudentId"
                  value={formik.values.typeStudentId}
                  onChange={formik.handleChange}
                  label="Tipo de Alumno *"
                >
                  <MenuItem value={0}>Seleccionar...</MenuItem>
                  {STUDENT_TYPES.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.description}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.typeStudentId && formik.errors.typeStudentId && (
                  <FormHelperText>{formik.errors.typeStudentId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Sección 2: Datos del Alumno */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                2. Datos del Alumno
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <PatternFormat
                customInput={TextField}
                format="##-########-#"
                mask="_"
                fullWidth
                id="cuil"
                name="cuil"
                label="CUIL *"
                value={formik.values.cuil}
                onValueChange={(values) => {
                  formik.setFieldValue("cuil", values.formattedValue);
                }}
                error={formik.touched.cuil && Boolean(formik.errors.cuil)}
                helperText={formik.touched.cuil && formik.errors.cuil}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="firstName"
                name="firstName"
                label="Nombre *"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onKeyDown={onlyLetters}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="lastName"
                name="lastName"
                label="Apellido *"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onKeyDown={onlyLetters}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email *"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <DesktopDatePicker
                  id="birthDate"
                  name="birthDate"
                  disableFuture
                  label="Fecha de Nacimiento *"
                  inputFormat="DD/MM/YYYY"
                  value={formik.values.birthDate}
                  onChange={(value) => formik.setFieldValue("birthDate", value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      error={formik.touched.birthDate && Boolean(formik.errors.birthDate)}
                      helperText={formik.touched.birthDate && formik.errors.birthDate}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                error={formik.touched.genderId && Boolean(formik.errors.genderId)}
              >
                <InputLabel>Género *</InputLabel>
                <Select
                  id="genderId"
                  name="genderId"
                  value={formik.values.genderId}
                  onChange={formik.handleChange}
                  label="Género *"
                >
                  <MenuItem value={0}>Seleccionar...</MenuItem>
                  {typeTables?.TBLGENDER?.data?.map((gender) => (
                    <MenuItem key={gender.id} value={gender.id}>
                      {gender.description}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.genderId && formik.errors.genderId && (
                  <FormHelperText>{formik.errors.genderId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                error={formik.touched.nationality && Boolean(formik.errors.nationality)}
              >
                <InputLabel>Nacionalidad *</InputLabel>
                <Select
                  id="nationality"
                  name="nationality"
                  value={formik.values.nationality}
                  onChange={formik.handleChange}
                  label="Nacionalidad *"
                >
                  <MenuItem value={0}>Seleccionar...</MenuItem>
                  {typeTables?.TBLNATIONALITYS?.data?.map((nationality) => (
                    <MenuItem key={nationality.id} value={nationality.id}>
                      {nationality.description}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.nationality && formik.errors.nationality && (
                  <FormHelperText>{formik.errors.nationality}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                error={formik.touched.academicId && Boolean(formik.errors.academicId)}
              >
                <InputLabel>Nivel Académico *</InputLabel>
                <Select
                  id="academicId"
                  name="academicId"
                  value={formik.values.academicId}
                  onChange={formik.handleChange}
                  label="Nivel Académico *"
                >
                  <MenuItem value={0}>Seleccionar...</MenuItem>
                  {typeTables?.TBLACADEMICS?.data?.map((academic) => (
                    <MenuItem key={academic.id} value={academic.id}>
                      {academic.description}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.academicId && formik.errors.academicId && (
                  <FormHelperText>{formik.errors.academicId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                error={formik.touched.labouralSituationId && Boolean(formik.errors.labouralSituationId)}
              >
                <InputLabel>Situación Laboral *</InputLabel>
                <Select
                  id="labouralSituationId"
                  name="labouralSituationId"
                  value={formik.values.labouralSituationId}
                  onChange={formik.handleChange}
                  label="Situación Laboral *"
                >
                  <MenuItem value={0}>Seleccionar...</MenuItem>
                  {typeTables?.TBLLABOURSITUATIONS?.data?.map((situation) => (
                    <MenuItem key={situation.id} value={situation.id}>
                      {situation.description}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.labouralSituationId && formik.errors.labouralSituationId && (
                  <FormHelperText>{formik.errors.labouralSituationId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                error={formik.touched.situationId && Boolean(formik.errors.situationId)}
              >
                <InputLabel>Situación ante UTEDYC *</InputLabel>
                <Select
                  id="situationId"
                  name="situationId"
                  value={formik.values.situationId}
                  onChange={formik.handleChange}
                  label="Situación ante UTEDYC *"
                >
                  <MenuItem value={0}>Seleccionar...</MenuItem>
                  {typeTables?.TBLSITUATIONS?.data
                    ?.filter((s) => VALID_SITUATION_IDS.includes(s.id))
                    .map((situation) => (
                      <MenuItem key={situation.id} value={situation.id}>
                        {situation.description}
                      </MenuItem>
                    ))}
                </Select>
                {formik.touched.situationId && formik.errors.situationId && (
                  <FormHelperText>{formik.errors.situationId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Sección 3: Acceso al Sistema */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                3. Nombre de Usuario (opcional)
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="username"
                name="username"
                label="Nombre de Usuario"
                value={formik.values.username}
                onChange={formik.handleChange}
                error={formik.touched.username && Boolean(formik.errors.username)}
                helperText={
                  formik.touched.username && formik.errors.username
                    ? formik.errors.username
                    : "Opcional. Si lo dejás vacío, el sistema lo genera automáticamente desde el email"
                }
                size="small"
                placeholder="Ejemplo: juan.perez"
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                La contraseña se generará automáticamente y será enviada al correo electrónico del alumno
              </Alert>
            </Grid>

            {/* Sección 4: Domicilio */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                4. Domicilio y Contacto
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                id="fullAddress"
                name="fullAddress"
                label="Dirección Completa *"
                value={formik.values.fullAddress}
                onChange={formik.handleChange}
                error={formik.touched.fullAddress && Boolean(formik.errors.fullAddress)}
                helperText={formik.touched.fullAddress && formik.errors.fullAddress}
                size="small"
                placeholder="Ej: Av. Corrientes 1234, Piso 5, Dpto A"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                size="small"
                error={formik.touched.provinceId && Boolean(formik.errors.provinceId)}
              >
                <InputLabel>Provincia *</InputLabel>
                <Select
                  id="provinceId"
                  name="provinceId"
                  value={formik.values.provinceId}
                  onChange={formik.handleChange}
                  label="Provincia *"
                >
                  <MenuItem value={0}>Seleccionar...</MenuItem>
                  {typeTables?.TBLPROVINCES?.data?.map((province) => (
                    <MenuItem key={province.id} value={province.id}>
                      {province.description}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.provinceId && formik.errors.provinceId && (
                  <FormHelperText>{formik.errors.provinceId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                id="areaCode"
                name="areaCode"
                label="Código de Área *"
                value={formik.values.areaCode}
                onChange={formik.handleChange}
                error={formik.touched.areaCode && Boolean(formik.errors.areaCode)}
                helperText={formik.touched.areaCode && formik.errors.areaCode}
                size="small"
                placeholder="011"
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                label="Teléfono *"
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                size="small"
                placeholder="1534567890"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            {/* Sección 5: Relación Familiar (SOLO para FAMILIARES) */}
            {(formik.values.situationId === 4 || formik.values.situationId === 5) && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                    5. Empleado o Afiliado Relacionado
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    Buscá al empleado o afiliado relacionado por CUIL/DNI. Si no lo encontrás en el sistema, podés continuar ingresando su nombre manualmente.
                  </Alert>
                </Grid>

                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Buscar Empleado/Afiliado por CUIL o DNI"
                    value={relatedSearchTerm}
                    onChange={(e) => {
                      setRelatedSearchTerm(e.target.value);
                      // Al cambiar el término de búsqueda, limpiar selección previa
                      if (formik.values.relatedClientId) {
                        formik.setFieldValue("relatedClientId", null);
                        formik.setFieldValue("relationshipType", 0);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearchRelatedClient();
                      }
                    }}
                    size="small"
                    placeholder="Ingrese CUIL o DNI del empleado o afiliado..."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSearchRelatedClient}
                    disabled={searchingRelated || !relatedSearchTerm.trim()}
                    startIcon={searchingRelated ? <CircularProgress size={20} color="inherit" /> : <Search />}
                    sx={{ height: "40px" }}
                  >
                    {searchingRelated ? "Buscando..." : "BUSCAR"}
                  </Button>
                </Grid>

                {/* Resultados de la búsqueda */}
                {relatedClients.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Resultados encontrados ({relatedClients.length}):
                      </Typography>
                      {relatedClients.map((client) => (
                        <Paper
                          key={client.clientId}
                          elevation={formik.values.relatedClientId === client.clientId ? 3 : 0}
                          sx={{
                            p: 2,
                            mb: 1,
                            cursor: "pointer",
                            border: formik.values.relatedClientId === client.clientId
                              ? "2px solid"
                              : "1px solid",
                            borderColor: formik.values.relatedClientId === client.clientId
                              ? "primary.main"
                              : "grey.300",
                            backgroundColor: formik.values.relatedClientId === client.clientId
                              ? "primary.50"
                              : "background.paper",
                            "&:hover": {
                              backgroundColor: formik.values.relatedClientId === client.clientId
                                ? "primary.50"
                                : "grey.50",
                              borderColor: "primary.main",
                            },
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => {
                            formik.setFieldValue("relatedClientId", client.clientId);
                            formik.setFieldValue("relatedClientName", "");
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {client.fullName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                CUIL: {client.cuil || client.document}
                              </Typography>
                              <Chip
                                label={client.typeDescription}
                                size="small"
                                color={
                                  client.situationId === 1 ? "primary" :
                                    client.situationId === 6 ? "secondary" :
                                      "default"
                                }
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                            {formik.values.relatedClientId === client.clientId && (
                              <CheckCircle color="primary" sx={{ fontSize: 28 }} />
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Paper>
                  </Grid>
                )}

                {/* Sin resultados: permitir carga manual */}
                {relatedClients.length === 0 && hasSearchedRelated && !searchingRelated && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No se encontró la persona en el sistema. Podés igualmente continuar completando el nombre manualmente.
                    </Alert>
                    <TextField
                      fullWidth
                      id="relatedClientName"
                      name="relatedClientName"
                      label="Nombre del relacionado (carga manual)"
                      value={formik.values.relatedClientName}
                      onChange={formik.handleChange}
                      error={formik.touched.relatedClientName && Boolean(formik.errors.relatedClientName)}
                      helperText={
                        formik.touched.relatedClientName && formik.errors.relatedClientName
                          ? formik.errors.relatedClientName
                          : "Nombre completo del Afiliado o Empleado al que se vincula"
                      }
                      size="small"
                      placeholder="Ej: Juan Pérez"
                    />
                  </Grid>
                )}

                {/* Tipo de relación: solo visible cuando hay un clientId seleccionado */}
                {formik.values.relatedClientId && (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={formik.touched.relationshipType && Boolean(formik.errors.relationshipType)}
                    >
                      <InputLabel>Tipo de Relación *</InputLabel>
                      <Select
                        id="relationshipType"
                        name="relationshipType"
                        value={formik.values.relationshipType}
                        onChange={formik.handleChange}
                        label="Tipo de Relación *"
                      >
                        <MenuItem value={0}>Seleccionar...</MenuItem>
                        {RELATIONSHIP_TYPES.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.description}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.relationshipType && formik.errors.relationshipType && (
                        <FormHelperText>{formik.errors.relationshipType}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </>
            )}

            {/* Botones */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleGoBack}
                  startIcon={<ArrowBack />}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
                  disabled={isSubmitting}
                  size="large"
                >
                  {isSubmitting ? "Guardando..." : "Guardar Alumno"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default DirectStudentRegistration;
